import { DEFAULT_ADMISSION_PROFILE_PAYLOAD } from './admissionProfileDefaults';

/** Fields the Provider write API accepts. PATCH replaces this whole object. */
export const ADMISSION_PROFILE_WRITE_KEYS = [
  'intended_treatment',
  'allowed_instrument_types',
  'legal_regimes',
  'control_profile',
  'mixed_packages_permitted',
  'primary_instrument_label',
  'business_context',
  'source_policy',
  'party_policy',
  'evidence_rules',
  'question_rules',
  'certification_policy',
  'attestation_version',
];

export const SERVER_OWNED_PROFILE_FIELDS = [
  'id',
  'program_id',
  'version',
  'status',
  'policy_hash',
  'created_by',
  'created_at',
  'approved_by',
  'approved_at',
  'activated_by',
  'activated_at',
  'superseded_at',
];

const SOURCE_SELECT_KEYS = [
  'required_source_form',
  'unknown_source_form',
  'mixed_document',
  'paper_original',
  'missing_parent',
];

const PARTY_TEXT_KEYS = ['control_party', 'legal_capacity', 'custodian'];

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function copyObject(value) {
  return isPlainObject(value) ? { ...value } : {};
}

function stringList(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => (item == null ? '' : String(item)));
}

export function prettyJson(value) {
  return JSON.stringify(isPlainObject(value) ? value : {}, null, 2);
}

export function profileToForm(profile) {
  const source = copyObject(profile?.source_policy);
  const party = copyObject(profile?.party_policy);
  const evidence = copyObject(profile?.evidence_rules);
  const questions = copyObject(profile?.question_rules);
  const items = Array.isArray(evidence.items) ? evidence.items : [];

  return {
    intended_treatment: profile?.intended_treatment || 'AUTHORITATIVE_CANDIDATE',
    mixed_packages_permitted: Boolean(profile?.mixed_packages_permitted),
    primary_instrument_label: profile?.primary_instrument_label || '',
    allowed_instrument_types: Array.isArray(profile?.allowed_instrument_types)
      ? [...profile.allowed_instrument_types]
      : [],
    legal_regimes: Array.isArray(profile?.legal_regimes) ? [...profile.legal_regimes] : [],
    control_profile: profile?.control_profile || 'NONE',
    source_policy: source,
    party_policy: party,
    evidence_items: items
      .filter((item) => isPlainObject(item))
      .map((item) => ({ ...item, code: item.code == null ? '' : String(item.code) })),
    evidence_extra: Object.fromEntries(
      Object.entries(evidence).filter(([key]) => key !== 'items')
    ),
    always_confirmed_facts: stringList(questions.always_confirmed_facts),
    required_confirmations: stringList(questions.required_confirmations),
    question_extra: Object.fromEntries(
      Object.entries(questions).filter(
        ([key]) => key !== 'always_confirmed_facts' && key !== 'required_confirmations'
      )
    ),
    attestation_version: profile?.attestation_version || 'UNSPECIFIED',
    business_context_text: prettyJson(profile?.business_context),
    certification_policy_text: prettyJson(profile?.certification_policy),
  };
}

function parseJsonObject(text, label, errors, key) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    errors[key] = `${label} must be valid JSON.`;
    return null;
  }
  if (!isPlainObject(parsed)) {
    errors[key] = `${label} must be a JSON object.`;
    return null;
  }
  return parsed;
}

function cleanStringList(values, label, errors, key) {
  const cleaned = [];
  values.forEach((value, index) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) {
      errors[`${key}-${index}`] = `Enter a fact code or remove this ${label} row.`;
      return;
    }
    cleaned.push(trimmed);
  });
  return cleaned;
}

/**
 * Build the full write body. Optional objects are sent as {} when cleared.
 * Unknown keys already on structured sections are copied through.
 */
export function buildAdmissionProfilePayload(form) {
  const errors = {};
  const label = String(form.primary_instrument_label || '');
  if (label.length > 255) {
    errors.primary_instrument_label = 'Primary instrument label must be 255 characters or fewer.';
  }
  const attestation = String(form.attestation_version || '').trim();
  if (!attestation) {
    errors.attestation_version = 'Attestation version is required.';
  } else if (attestation.length > 64) {
    errors.attestation_version = 'Attestation version must be 64 characters or fewer.';
  }

  const businessContext = parseJsonObject(
    form.business_context_text ?? '{}',
    'Business context',
    errors,
    'business_context'
  );
  const certificationPolicy = parseJsonObject(
    form.certification_policy_text ?? '{}',
    'Certification policy',
    errors,
    'certification_policy'
  );

  const sourcePolicy = copyObject(form.source_policy);
  SOURCE_SELECT_KEYS.forEach((key) => {
    const value = sourcePolicy[key];
    if (value == null || String(value).trim() === '') delete sourcePolicy[key];
  });
  if (sourcePolicy.real_property_link_required !== true) {
    delete sourcePolicy.real_property_link_required;
  }

  const partyPolicy = copyObject(form.party_policy);
  PARTY_TEXT_KEYS.forEach((key) => {
    const value = partyPolicy[key];
    if (value == null || String(value).trim() === '') delete partyPolicy[key];
    else partyPolicy[key] = String(value).trim();
  });

  const evidenceItems = [];
  (form.evidence_items || []).forEach((item, index) => {
    const code = String(item?.code || '').trim();
    if (!code) {
      errors[`evidence-${index}`] = 'Enter an evidence code or remove this row.';
      return;
    }
    const next = { ...(isPlainObject(item) ? item : {}), code };
    if (item?.required === false) next.required = false;
    else if (item?.required === true) next.required = true;
    else delete next.required;
    evidenceItems.push(next);
  });

  const alwaysConfirmed = cleanStringList(
    form.always_confirmed_facts || [],
    'always-confirmed fact',
    errors,
    'always'
  );
  const requiredConfirmations = cleanStringList(
    form.required_confirmations || [],
    'required confirmation',
    errors,
    'required'
  );

  if (Object.keys(errors).length) {
    return { ok: false, errors };
  }

  const payload = {
    intended_treatment: form.intended_treatment,
    allowed_instrument_types: [...(form.allowed_instrument_types || [])],
    legal_regimes: [...(form.legal_regimes || [])],
    control_profile: form.control_profile,
    mixed_packages_permitted: Boolean(form.mixed_packages_permitted),
    primary_instrument_label: label.trim(),
    business_context: businessContext,
    source_policy: sourcePolicy,
    party_policy: partyPolicy,
    evidence_rules: {
      ...(form.evidence_extra || {}),
      items: evidenceItems,
    },
    question_rules: {
      ...(form.question_extra || {}),
      always_confirmed_facts: alwaysConfirmed,
      required_confirmations: requiredConfirmations,
    },
    certification_policy: certificationPolicy,
    attestation_version: attestation,
  };

  SERVER_OWNED_PROFILE_FIELDS.forEach((key) => {
    delete payload[key];
  });

  return { ok: true, payload };
}

export function defaultAdmissionProfilePayload() {
  return JSON.parse(JSON.stringify(DEFAULT_ADMISSION_PROFILE_PAYLOAD));
}
